//
// Copyright (c) 2011-2019 by Curalate, Inc.
//

import {
  IValidateCatalogSourceRequest,
  IValidateCatalogSourceResponse
} from '@apis/catalog-validation/catalogValidationModel';
import { uploadFile, validateCatalogSource } from '@apis/catalog-validation/productCatalogValidation';
import { CATALOG_LOCALE_TYPES, CONNECT_PANEL_ACTIONS, CONNECTION_METHODS } from '@catalog-ingestion/constants/connectPanelConstants';
import { ICuralateStore } from '@models/AppState';
import { createAction } from '@utils/actions';
import { ReduxDispatch } from 'app/curalate.store';

const changeLocale = (selectedLocale: CATALOG_LOCALE_TYPES) => {
  return createAction({
    type: CONNECT_PANEL_ACTIONS.CHANGE_LOCALE,
    selectedLocale,
  });
};

const changeConnectionMethod = (connectionMethod: CONNECTION_METHODS) => {
  return createAction({
    type: CONNECT_PANEL_ACTIONS.CHANGE_CONNECTION_METHOD,
    connectionMethod,
  });
};

const toggleIsValidating = () => {
  return createAction({
    type: CONNECT_PANEL_ACTIONS.TOGGLE_IS_VALIDATING,
  });
};

const setIsUrlEmpty = (isUrlEmpty: boolean) => {
  return createAction({
    type: CONNECT_PANEL_ACTIONS.SET_IS_URL_EMPTY,
    isUrlEmpty,
  });
};

const setIsCatalogSourceInvalid = (isCatalogSourceInvalid: boolean) => {
  return createAction({
    type: CONNECT_PANEL_ACTIONS.SET_IS_CATALOG_SOURCE_VALID,
    isCatalogSourceInvalid,
  });
};

const setIsFileUploading = (isFileUploading: boolean) => {
  return createAction({
    type: CONNECT_PANEL_ACTIONS.SET_IS_FILE_UPLOADING,
    isFileUploading,
  });
};

const setDidFileUploadFail = (didFileUploadFail: boolean) => {
  return createAction({
    type: CONNECT_PANEL_ACTIONS.SET_DID_FILE_UPLOAD_FAIL,
    didFileUploadFail,
  });
};

const resetCatalogUrlCredentialsState = () => {
  return createAction({
    type: CONNECT_PANEL_ACTIONS.RESET_CATALOG_URL_CREDENTIALS_STATE,
  });
};

const changeLoginName = (newLoginName: string) => {
  return createAction({
    type: CONNECT_PANEL_ACTIONS.CHANGE_LOGIN,
    newLoginName,
  });
};

const changePassword = (newPassword: string) => {
  return createAction({
    type: CONNECT_PANEL_ACTIONS.CHANGE_PASSWORD,
    newPassword,
  });
};

const changeCatalogUrl = (newCatalogUrl: string) => {
  return createAction({
    type: CONNECT_PANEL_ACTIONS.CHANGE_URL,
    newCatalogUrl,
  });
};

const validateSource = () => {
  return createAction({
    type: CONNECT_PANEL_ACTIONS.REQUEST_VALIDATE_SOURCE,
  });
};

const receiveValidateSource = (catalogSource: IValidateCatalogSourceResponse) => {
  return createAction({
    type: CONNECT_PANEL_ACTIONS.RECEIVE_VALIDATE_SOURCE,
    catalogSource,
  });
};

export type ConnectPanelAction = ReturnType<
  typeof changeLocale |
  typeof changeConnectionMethod |
  typeof toggleIsValidating |
  typeof setIsUrlEmpty |
  typeof resetCatalogUrlCredentialsState |
  typeof changeLoginName |
  typeof changePassword |
  typeof changeCatalogUrl |
  typeof setIsCatalogSourceInvalid |
  typeof validateSource |
  typeof receiveValidateSource |
  typeof setIsFileUploading |
  typeof setDidFileUploadFail
>;

export const ConnectPanelActions = {
  changeLocale,
  changeConnectionMethod,
  toggleIsValidating,
  setIsUrlEmpty,
  resetCatalogUrlCredentialsState,
  changeLoginName,
  changePassword,
  changeCatalogUrl,
  setIsCatalogSourceInvalid,
  receiveValidateSource,
  setIsFileUploading,
  setDidFileUploadFail,

  validateSource: () => async (
    dispatch: ReduxDispatch,
    getState: () => ICuralateStore,
  ) => {
    const {
      loginName,
      password,
      catalogUrl,
    } = getState().catalogIngestionReducers.connectPanel;

    const catalogSource: IValidateCatalogSourceRequest = {
      location: catalogUrl,
    };

    if (loginName && password) {
      catalogSource.login = loginName;
      catalogSource.password = password;
    }

    dispatch(validateSource());
    try {
      const result = await validateCatalogSource(catalogSource);
      dispatch(receiveValidateSource(result));
      return result.succeeded;
    } catch (e) {
      return false;
    }
  },
  uploadFileToS3: (newFile: File) => async (
    dispatch: ReduxDispatch,
  ) => {
    dispatch(setIsFileUploading(true));
    const result = await uploadFile(newFile);
    if (result.s3path) {
      return result.s3path;
    } else {
      // null s3Path so the upload failed
      dispatch(setDidFileUploadFail(true));
    }
  },
};
